import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, Shield, ShieldAlert, Info, Check, CheckCheck, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { systemAlertsApi } from '../../services/api';

const SEVERITY_CONFIG = {
  critical: { icon: ShieldAlert, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-700', label: 'Crítico' },
  high:     { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', label: 'Alto' },
  medium:   { icon: Shield, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Médio' },
  info:     { icon: Info, color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-100 text-sky-700', label: 'Info' },
};

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

export default function AdminAlertBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [critical, setCritical] = useState(0);
  const [unackUrgent, setUnackUrgent] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      const data = await systemAlertsApi.getUnreadCount();
      setUnread(data.unread || 0);
      setCritical(data.critical || 0);
      setUnackUrgent(data.unacknowledged_urgent || 0);
    } catch { /* ignore */ }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await systemAlertsApi.list({ limit: 8, read: false });
      setAlerts(data.alerts || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchAlerts();
  }, [open, fetchAlerts]);

  const handleMarkAllRead = async () => {
    try {
      await systemAlertsApi.markAllRead();
      setUnread(0);
      setCritical(0);
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    } catch { /* ignore */ }
  };

  const handleMarkRead = async (alertId, e) => {
    e.stopPropagation();
    try {
      await systemAlertsApi.markRead(alertId);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, read: true } : a));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`p-2 hover:bg-white/10 rounded-lg relative ${unackUrgent > 0 ? 'animate-pulse' : ''}`}
          data-testid="admin-alert-bell"
          title={unackUrgent > 0 ? `${unackUrgent} alerta(s) urgente(s) sem reconhecimento` : 'Alertas do sistema'}
        >
          <Bell className={`w-5 h-5 ${unackUrgent > 0 ? 'text-red-300' : ''}`} />
          {unread > 0 && (
            <span
              className={`absolute -top-1 -right-1 min-w-[20px] h-5 text-xs font-bold rounded-full flex items-center justify-center px-1 ${
                critical > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-[#FFA52A] text-white'
              }`}
              data-testid="alert-bell-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          {unackUrgent > 0 && (
            <span
              className="absolute -bottom-1 -right-1 w-3 h-3 bg-red-600 rounded-full ring-2 ring-white animate-ping"
              data-testid="alert-bell-urgent-pulse"
            />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-hidden flex flex-col">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Alertas do Sistema
          </span>
          {unread > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-3 h-3" /> Marcar lidas
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="overflow-y-auto flex-1 max-h-[300px]">
          {alerts.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              Nenhum alerta pendente
            </div>
          ) : (
            alerts.map(alert => {
              const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
              const Icon = cfg.icon;
              return (
                <DropdownMenuItem
                  key={alert.id}
                  className={`flex items-start gap-3 p-3 cursor-pointer ${!alert.read ? cfg.bg : ''}`}
                  onClick={() => { setOpen(false); navigate('/admin/alertas-sistema'); }}
                  data-testid={`alert-item-${alert.id}`}
                >
                  <div className={`p-1.5 rounded-lg ${cfg.bg} ${cfg.border} border flex-shrink-0 mt-0.5`}>
                    <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-medium truncate ${!alert.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {alert.title}
                      </p>
                      {!alert.read && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />}
                    </div>
                    <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                      {alert.context?.user_name || alert.context?.entity_label || alert.description}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(alert.created_at)}</p>
                  </div>
                  {!alert.read && (
                    <button
                      onClick={(e) => handleMarkRead(alert.id, e)}
                      className="p-1 hover:bg-white/80 rounded flex-shrink-0"
                      title="Marcar como lida"
                    >
                      <Check className="w-3 h-3 text-muted-foreground" />
                    </button>
                  )}
                </DropdownMenuItem>
              );
            })
          )}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-center justify-center text-xs text-muted-foreground"
          onClick={() => { setOpen(false); navigate('/admin/alertas-sistema'); }}
          data-testid="view-all-alerts-btn"
        >
          <ExternalLink className="w-3 h-3 mr-1" /> Ver todos os alertas
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
